import React, { useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { checkSession } from '../api/authService';
import { updateUserTimezone } from '../api/dataService';
import { useLocale } from '../context/LocaleContext';
import { detectBrowserTimezone } from '../utils/timezone';

const GoogleCallbackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useContext(AuthContext);
  const { t } = useLocale();

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const queryParams = new URLSearchParams(location.search);
        const token = queryParams.get('token');

        if (!token) {
          throw new Error('Missing token in callback URL');
        }

        // Store only the token first, then hydrate the full backend user before routing.
        // This avoids gating or redirect decisions based on an incomplete placeholder user.
        localStorage.setItem('user', JSON.stringify({ token }));

        const sessionData = await checkSession();
        if (!sessionData?.user) {
          throw new Error('Session hydration failed after OAuth callback');
        }

        setUser(sessionData.user);

        const browserTimezone = detectBrowserTimezone();
        if (browserTimezone) {
          try {
            await updateUserTimezone(browserTimezone);
          } catch (tzErr) {
            console.warn('Failed to update timezone during Google login.', tzErr);
          }
        }

        if (sessionData.user.onboardingComplete === false) {
          navigate('/onboarding', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        console.error('Error during Google OAuth callback:', err);
        localStorage.removeItem('user');
        navigate('/login?error=callback_failed');
      }
    };

    handleCallback();
  }, [location, navigate, setUser]);


  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black text-white">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-4"></div>
      <h2 className="text-xl font-semibold">
        {t('googleCallback.processing', 'Processing login...')}
      </h2>
    </div>
  );
};

export default GoogleCallbackPage;
